// Logic based on https://nikitahl.github.io/svg-circle-progress-generator/
import {
  ProgressBar as AriaProgressBar,
  type ProgressBarProps as AriaProgressBarProps,
} from "react-aria-components"
import { twMerge } from "tailwind-merge"

export type BaseProgressRingProps = Omit<AriaProgressBarProps, "value"> & {
  animate?: boolean
  diameter?: number
  label: string
  maxValue?: number
  minValue?: number
  progress?: number
  strokeWidth?: number
  svgProps?: React.SVGProps<SVGSVGElement>
  trackClassName?: string
  progressClassName?: string
}

export const BaseProgressRing = ({
  animate,
  diameter = 100,
  label,
  maxValue = 100,
  minValue = 0,
  progress = 25,
  strokeWidth = 4,
  trackClassName,
  progressClassName,
  ...props
}: BaseProgressRingProps) => {
  const radius = (diameter - strokeWidth) / 2
  const circumference = 2 * Math.PI * radius

  return (
    <AriaProgressBar
      value={progress}
      minValue={minValue}
      maxValue={maxValue}
      aria-label={label}
      style={{
        width: `${diameter}px`,
        height: `${diameter}px`,
        flexShrink: 0,
      }}
      {...props}
    >
      {({ percentage = 0 }) => {
        const strokeDashoffset = circumference * ((100 - percentage) / 100)

        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox={`0 0 ${diameter} ${diameter}`}
            width={diameter}
            height={diameter}
            className={twMerge(animate && "tt-animate-spin")}
          >
            <circle
              cx={diameter / 2}
              cy={diameter / 2}
              r={radius}
              fill="transparent"
              strokeWidth={strokeWidth}
              strokeDasharray={circumference}
              strokeDashoffset="0"
              className={twMerge("stroke-neutral-500/10", trackClassName)}
            />
            <circle
              className={twMerge(
                "tt-progress-ring-inner stroke-fg-default",
                progressClassName
              )}
              cx={diameter / 2}
              cy={diameter / 2}
              r={radius}
              fill="transparent"
              strokeWidth={strokeWidth}
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              style={{
                transform: "rotate(-90deg)",
                transformOrigin: "50% 50%",
              }}
            />
          </svg>
        )
      }}
    </AriaProgressBar>
  )
}
